import { WidgetDefinition } from "@/types/dashboard";
import {
  IndianRupee, TrendingUp, TrendingDown, CreditCard, Wallet,
  Receipt, Clock, CheckCircle2, AlertTriangle, AlertCircle,
  Package, ShoppingBag, BarChart3, PieChart as PieIcon, LineChart as LineIcon,
  Activity, Zap, Users, Landmark, Layers, Calendar, Sparkles,
  ArrowUpRight, ArrowDownRight, ShieldCheck, Box, RefreshCw
} from "lucide-react";

export const WIDGET_REGISTRY: Record<string, WidgetDefinition & { icon: any }> = {
  // ── Financial KPI Cards ──────────────────────────────────────────────────
  revenue: {
    widgetId: 'revenue',
    title: 'Total Revenue',
    description: 'Net taxable sales revenue generated in the selected period after returns',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: IndianRupee,
  },
  net_revenue: {
    widgetId: 'net_revenue',
    title: 'Net Revenue',
    description: 'Gross sales minus sales returns and trade discounts excluding taxes',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: TrendingUp,
  },
  gross_profit: {
    widgetId: 'gross_profit',
    title: 'Gross Profit',
    description: 'Revenue remaining after deducting direct Cost of Goods Sold (COGS)',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: ArrowUpRight,
  },
  net_profit: {
    widgetId: 'net_profit',
    title: 'Net Profit',
    description: 'Bottom-line earnings after deducting operating expenses and overheads',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Sparkles,
  },
  profit_margin: {
    widgetId: 'profit_margin',
    title: 'Profit Margin',
    description: 'Percentage of net revenue retained as bottom-line profit',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: TrendingUp,
  },
  total_expenses: {
    widgetId: 'total_expenses',
    title: 'Total Expenses',
    description: 'Operating overheads and non-inventory indirect costs in this period',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: CreditCard,
  },
  cogs: {
    widgetId: 'cogs',
    title: 'COGS (Cost of Goods)',
    description: 'Direct inventory acquisition costs recognized on sold goods',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Package,
  },
  bank_balance: {
    widgetId: 'bank_balance',
    title: 'Bank Balance',
    description: 'Live reconciled aggregate balance across all active company bank accounts',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Landmark,
  },
  cash_in_hand: {
    widgetId: 'cash_in_hand',
    title: 'Cash in Hand',
    description: 'Petty cash and cash counter balances held locally',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Wallet,
  },
  receivables: {
    widgetId: 'receivables',
    title: 'Accounts Receivable',
    description: 'Outstanding customer dues across all Sundry Debtors ledgers',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: ArrowUpRight,
  },
  payables: {
    widgetId: 'payables',
    title: 'Accounts Payable',
    description: 'Pending vendor liabilities across all Sundry Creditors ledgers',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: ArrowDownRight,
  },
  overdue_invoices: {
    widgetId: 'overdue_invoices',
    title: 'Overdue Invoices',
    description: 'Unpaid sales invoices that have exceeded standard payment terms (>30 days)',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Clock,
  },
  upcoming_payments: {
    widgetId: 'upcoming_payments',
    title: 'Upcoming Payments',
    description: 'Pending payment vouchers and vendor disbursements awaiting clearance',
    category: 'financial',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Calendar,
  },
  gst_liability: {
    widgetId: 'gst_liability',
    title: 'Net GST Liability',
    description: 'Output tax payable in cash after offsetting eligible Input Tax Credit (ITC)',
    category: 'gst',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: ShieldCheck,
  },
  gst_itc: {
    widgetId: 'gst_itc',
    title: 'GST Input Credit (ITC)',
    description: 'Eligible ITC on business purchases available or carried forward',
    category: 'gst',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: ShieldCheck,
  },

  // ── Operations & Charts ──────────────────────────────────────────────────
  quick_actions: {
    widgetId: 'quick_actions',
    title: 'Quick Actions',
    description: 'Instant launch buttons for high-frequency accounting workflows',
    category: 'operations',
    defaultWidth: 'full',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: Zap,
  },
  sales_expenses_chart: {
    widgetId: 'sales_expenses_chart',
    title: 'Sales vs Expenses',
    description: 'Monthly comparison of Sales revenue, Operating expenses, and Profit',
    category: 'charts',
    defaultWidth: 'wide',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: BarChart3,
  },
  expense_breakdown: {
    widgetId: 'expense_breakdown',
    title: 'Expense Breakdown',
    description: 'Donut chart showing proportional overhead spending by category',
    category: 'charts',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: PieIcon,
  },
  cash_flow_chart: {
    widgetId: 'cash_flow_chart',
    title: 'Cash Flow Trend',
    description: 'Realized monetary cash inflows vs disbursements over time',
    category: 'charts',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: LineIcon,
  },
  profit_loss_trends: {
    widgetId: 'profit_loss_trends',
    title: 'Profit & Loss Trends',
    description: 'Longitudinal view of gross margin and operating margin trajectory',
    category: 'charts',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: TrendingUp,
  },
  activity_feed: {
    widgetId: 'activity_feed',
    title: 'Business Activity Feed',
    description: 'Real-time ledger audit trail showing recent transactions and approvals',
    category: 'operations',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: Activity,
  },
  recent_transactions: {
    widgetId: 'recent_transactions',
    title: 'Recent Transactions',
    description: 'Latest approved and pending sales, purchase, and receipt vouchers',
    category: 'operations',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide'],
    icon: Receipt,
  },
  live_clock: {
    widgetId: 'live_clock',
    title: 'Live Clock (IST)',
    description: 'Real-time India Standard Time (IST) clock with interactive Digital and Analog display modes',
    category: 'operations',
    defaultWidth: 'medium',
    supportedWidths: ['compact', 'medium', 'wide'],
    icon: Clock,
  },

  // ── Inventory Widgets ────────────────────────────────────────────────────
  inventory_valuation: {
    widgetId: 'inventory_valuation',
    title: 'Inventory Valuation',
    description: 'Total perpetual asset value of stocked goods currently on hand',
    category: 'inventory',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Box,
  },
  low_stock_alerts: {
    widgetId: 'low_stock_alerts',
    title: 'Low Stock Alerts',
    description: 'Items at or below defined minimum reorder threshold',
    category: 'inventory',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: AlertTriangle,
  },
  out_of_stock: {
    widgetId: 'out_of_stock',
    title: 'Out of Stock Items',
    description: 'Catalog products currently with zero quantity on hand',
    category: 'inventory',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: AlertCircle,
  },
  fast_moving: {
    widgetId: 'fast_moving',
    title: 'Fast-Moving Products',
    description: 'Top products ranked by sales velocity and unit turnover',
    category: 'inventory',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Zap,
  },
  slow_moving: {
    widgetId: 'slow_moving',
    title: 'Slow-Moving Products',
    description: 'Inventory items with sluggish velocity tying up working capital',
    category: 'inventory',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Clock,
  },

  // ── Insights & Intelligence ──────────────────────────────────────────────
  customer_insights: {
    widgetId: 'customer_insights',
    title: 'Customer Insights',
    description: 'Top clients ranked by invoice revenue and outstanding credit balances',
    category: 'insights',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide'],
    icon: Users,
  },
  supplier_insights: {
    widgetId: 'supplier_insights',
    title: 'Supplier Insights',
    description: 'Top vendors by purchase volume and pending payable amounts',
    category: 'insights',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide'],
    icon: ShoppingBag,
  },
  monthly_comparison: {
    widgetId: 'monthly_comparison',
    title: 'Monthly Comparison',
    description: 'Head-to-head performance audit between current and previous periods',
    category: 'insights',
    defaultWidth: 'medium',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: RefreshCw,
  },
  gst_deadlines: {
    widgetId: 'gst_deadlines',
    title: 'Upcoming GST Deadlines',
    description: 'Filing countdowns for GSTR-1, GSTR-3B, and quarterly tax statements',
    category: 'gst',
    defaultWidth: 'compact',
    supportedWidths: ['compact', 'medium'],
    icon: Calendar,
  },
  health_score: {
    widgetId: 'health_score',
    title: 'Financial Health Score',
    description: 'Auditable 0-100 gauge reflecting liquidity, efficiency, and margins',
    category: 'ai',
    defaultWidth: 'medium',
    supportedWidths: ['compact', 'medium', 'wide'],
    icon: ShieldCheck,
  },
  ai_advisor: {
    widgetId: 'ai_advisor',
    title: 'AI Business Advisor',
    description: 'Verified mathematical recommendations with auditable proof and savings',
    category: 'ai',
    defaultWidth: 'full',
    supportedWidths: ['medium', 'wide', 'full'],
    icon: Sparkles,
  },
  ai_insights: {
    widgetId: 'ai_insights',
    title: 'AI Financial Insights',
    description: 'Executive briefing strip summarizing critical ledger and tax events',
    category: 'ai',
    defaultWidth: 'full',
    supportedWidths: ['full'],
    icon: Sparkles,
  },
};
